import type { JSX } from "react";
import { Suspense, lazy, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, Minus, MoreVertical, Pencil, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useRecipe } from "./useRecipe.js";
import { useServingsScale } from "./useServingsScale.js";
import { sync } from "../../sync/SyncService.js";
import { db } from "../../sync/db.js";
import { friendlyError } from "../../shared/http.js";
import { Button } from "@/ui/button.js";
import { setFocusedSessionId } from "../cook-sessions/focusedSession.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu.js";
import { localDateInZone, type TaxonomyApiItem, type TaxonomyKind } from "@recipevault/shared";
import { KIND_CHIP_CLASS } from "./taxonomyKindStyle.js";
import { RecipePhotoImg } from "./RecipePhotoImg.js";
import MetadataPills from "./MetadataPills.js";
import { InsightsStrip } from "./InsightsStrip.js";
import { SendToShoppingSheet } from "./SendToShoppingSheet.js";
import { formatFactor, formatQty } from "./ingredientFormat.js";
import { descSnippet, hasMoreContent } from "./descSnippet.js";
import { useHouseholdTimezone } from "../auth/useHouseholdTimezone.js";
import { detectPlanRef } from "../meal-plans/planLinkage.js";
const MarkdownView = lazy(() => import("./MarkdownView.js"));

function useTaxonomyMap(): Map<string, TaxonomyApiItem> {
  const all = useLiveQuery(async () => db.taxonomy.toArray(), [], [] as TaxonomyApiItem[]);
  return new Map((all ?? []).map((t) => [t.id, t]));
}

interface RecipeViewProps {
  householdId: string;
}

export function RecipeView({ householdId }: RecipeViewProps): JSX.Element {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const { recipe, status } = useRecipe(id);
  const taxonomyMap = useTaxonomyMap();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [starting, setStarting] = useState(false);
  // Task 13: open/closed state for the send-to-shopping bottom sheet.
  const [shopOpen, setShopOpen] = useState(false);
  // Task 14: has the user tapped "more" to expand the clamped description?
  const [descExpanded, setDescExpanded] = useState(false);
  // Task 14: household tz, needed by InsightsStrip's "last made N days ago".
  // Read unconditionally here (Rules of Hooks) — used below, after the
  // loading/not-found early-returns, once `recipe` is guaranteed non-null.
  const tz = useHouseholdTimezone();
  // Task 12: display-only servings scaler. Hooks must be called unconditionally,
  // so this runs before the loading/not-found early-returns below — `recipe` may
  // still be undefined on the very first render, hence the `recipe ? … : null`
  // guard. `factor` stays 1 until the recipe has loaded and the user interacts,
  // which is the correct default either way. Task 13 (send-to-shopping) reads
  // `factor` from this same scope.
  const { target, factor, setTarget, reset } = useServingsScale(recipe ? recipe.servings : null);
  const navigate = useNavigate();

  if (status === "loading") return <p className="text-zinc-500 text-sm">Loading…</p>;
  if (status === "not-found" || !recipe) {
    return (
      <div className="py-12 text-center">
        <div className="text-zinc-300 text-lg font-medium mb-2">Recipe not found</div>
        <Link to="/recipes" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 text-sm">
          Back to library
        </Link>
      </div>
    );
  }

  async function startCooking(): Promise<void> {
    if (starting || !recipe) return;
    setStarting(true);
    try {
      // Plan 14: unlike Tonight (which already knows the tapped slot),
      // RecipeView is reached from the library/search/anywhere — so it has to
      // auto-detect whether today's plan already queued this recipe.
      const planRef = await detectPlanRef(recipe.id, tz);
      const session = await sync.createCookSessionOptimistic({
        recipeId: recipe.id,
        ...(planRef ? { planRef } : {}),
      });
      setFocusedSessionId(session.id);
      void navigate("/cook/" + session.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start cooking");
      setStarting(false); // re-enable on failure (on success we navigate away)
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!recipe) return;
    setDeleting(true);
    try {
      await sync.deleteRecipeOptimistic(recipe.id);
      toast.success(`Deleted '${recipe.title}'`);
      setConfirmOpen(false);
      void navigate("/recipes");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setDeleting(false);
    }
  }

  async function toggleWantToTry(): Promise<void> {
    if (!recipe) return;
    try {
      await sync.patchRecipeOptimistic(recipe.id, {
        version: recipe.version,
        wantToTry: !recipe.wantToTry,
      });
    } catch (e) {
      toast.error(friendlyError(e));
    }
  }

  const taxonomyChips: { kind: TaxonomyKind; label: string }[] = [
    ...recipe.taxonomy.tagIds.map((id) => ({ kind: "tag" as const, label: taxonomyMap.get(id)?.name ?? "unknown tag" })),
    ...recipe.taxonomy.categoryIds.map((id) => ({ kind: "category" as const, label: taxonomyMap.get(id)?.name ?? "unknown category" })),
    ...(recipe.taxonomy.cuisineId
      ? [{ kind: "cuisine" as const, label: taxonomyMap.get(recipe.taxonomy.cuisineId)?.name ?? "unknown cuisine" }]
      : []),
    ...recipe.taxonomy.dietFlags.map((id) => ({ kind: "diet" as const, label: taxonomyMap.get(id)?.name ?? "unknown diet" })),
  ];

  // Fallback covers the render right after the recipe first loads (see the
  // Task-12 hook comment above) — `recipe.servings` is guaranteed a number
  // whenever the stepper below is actually rendered.
  const displayServings = target ?? recipe.servings ?? 1;

  // Task 14: description clamp + insights strip.
  const today = localDateInZone(tz, new Date());
  const descriptionSnippet = descSnippet(recipe.description);
  // A "more" expander only makes sense when there's BOTH a snippet to show
  // collapsed AND additional content beyond it — a single-paragraph
  // description (snippet === the whole trimmed text) renders in full
  // immediately, with nothing to expand to.
  const descriptionClampable = descriptionSnippet !== null && hasMoreContent(recipe.description);

  return (
    <>
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {/* Back arrow always goes to the library. Using navigate(-1) was
                fragile: after Library → View → Edit → save → View, browser-back
                lands on Edit instead of Library. The library still loads with
                its persisted URL-state filters because the Link is to="/recipes"
                and React Router restores scroll/state on Link clicks. */}
            <Link
              to="/recipes"
              aria-label="Back to recipes"
              className="shrink-0 text-zinc-400 hover:text-zinc-200 rounded p-1 -ml-1 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </Link>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-50 min-w-0 truncate">
              {recipe.title}
            </h1>
          </div>
          {taxonomyChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {taxonomyChips.map((c, idx) => (
                <span
                  key={`${c.kind}:${String(idx)}`}
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs ${KIND_CHIP_CLASS[c.kind]}`}
                >
                  {c.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {(recipe.photos.coverKey || recipe.photos.extras.length > 0) && (
        <section className="space-y-3" aria-label="Photos">
          {recipe.photos.coverKey && (
            <RecipePhotoImg
              householdId={householdId}
              recipeId={recipe.id}
              photoKey={recipe.photos.coverKey}
              size="full"
              alt={`Cover for ${recipe.title}`}
              className="w-full max-h-96 rounded-lg object-cover"
            />
          )}
          {recipe.photos.extras.length > 0 && (
            <ul className="flex gap-2 overflow-x-auto pb-2">
              {recipe.photos.extras.map((photoKey) => (
                <li key={photoKey} className="flex-shrink-0">
                  <RecipePhotoImg
                    householdId={householdId}
                    recipeId={recipe.id}
                    photoKey={photoKey}
                    size="thumb"
                    alt={`Photo for ${recipe.title}`}
                    className="h-24 w-24 rounded-md object-cover"
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Plan 08: metadata pills + description above ingredients */}
      <MetadataPills
        servings={recipe.servings}
        totalTimeMin={recipe.totalTimeMin}
        activeTimeMin={recipe.activeTimeMin}
        difficulty={recipe.difficulty}
        rating={recipe.rating}
      />

      {/* Task 14: one-line insights strip — cooked N×, last made, usual time. */}
      <InsightsStrip stats={recipe.stats} today={today} tzid={tz} />

      {recipe.description !== null && recipe.description.trim().length > 0 && (
        <section data-testid="recipe-description" className="text-zinc-200">
          {descriptionClampable && !descExpanded ? (
            <>
              <p>{descriptionSnippet}</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-1 h-auto min-h-[48px] px-0 text-emerald-400 hover:bg-transparent hover:text-emerald-300"
                onClick={() => { setDescExpanded(true); }}
              >
                more
              </Button>
            </>
          ) : (
            <Suspense fallback={<p className="text-sm text-zinc-500">Loading…</p>}>
              <MarkdownView source={recipe.description} />
            </Suspense>
          )}
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-zinc-50 font-medium">Ingredients</h2>
        {/* Task 12: display-only servings scaler — pure local UI state, never
            persisted. `factor` multiplies the shown quantities below; Task 13
            reads `factor` from this same RecipeView scope for send-to-shopping. */}
        {recipe.servings !== null && (
          <div className="flex flex-wrap items-center gap-3" data-testid="servings-stepper">
            <Button
              type="button"
              variant="outline"
              size="icon-xl"
              aria-label="Decrease servings"
              onClick={() => { setTarget(displayServings - 1); }}
            >
              <Minus aria-hidden />
            </Button>
            <span className="min-w-24 text-center font-medium tabular-nums text-zinc-100">
              {displayServings} {displayServings === 1 ? "serving" : "servings"}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon-xl"
              aria-label="Increase servings"
              onClick={() => { setTarget(displayServings + 1); }}
            >
              <Plus aria-hidden />
            </Button>
            {factor !== 1 && (
              <span className="text-xs text-zinc-500">
                {`scaled ×${formatFactor(factor)} · `}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-zinc-300"
                  onClick={reset}
                >
                  reset
                </button>
              </span>
            )}
          </div>
        )}
        {recipe.ingredients.length === 0 ? (
          <p className="text-zinc-500 text-sm">No ingredients yet.</p>
        ) : (
          <ul className="space-y-1">
            {recipe.ingredients.map((i) => {
              const qty = i.qty == null ? "" : formatQty(i.qty * factor);
              const unit = i.unit ?? "";
              const amount = [qty, unit].filter(Boolean).join(" ");
              const text = i.raw?.trim() || i.name || "";
              return (
                <li key={i.id} className="text-zinc-300 text-sm flex gap-3">
                  <span
                    data-testid={`ingredient-amount-${i.id}`}
                    className="shrink-0 w-20 text-right font-medium text-zinc-100 tabular-nums"
                    aria-hidden={amount === "" || undefined}
                  >
                    {amount}
                  </span>
                  <span className="flex-1 min-w-0">{text}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-zinc-50 font-medium">Steps</h2>
        {recipe.steps.length === 0 ? (
          <p className="text-zinc-500 text-sm">No steps yet.</p>
        ) : (
          <ol className="space-y-2 list-decimal list-inside">
            {recipe.steps.map((s) => {
              // Tolerate legacy rows stored before imageKeys existed.
              const imageKeys = s.imageKeys ?? [];
              return (
              <li key={s.id} className="text-zinc-300 text-sm whitespace-pre-wrap">
                {s.text}
                {imageKeys.length > 0 && (
                  <ul className="mt-2 flex gap-2 overflow-x-auto pb-1">
                    {imageKeys.map((photoKey) => (
                      <li key={photoKey} className="flex-shrink-0">
                        <RecipePhotoImg
                          householdId={householdId}
                          recipeId={recipe.id}
                          photoKey={photoKey}
                          size="thumb"
                          alt={`Step photo for ${recipe.title}`}
                          className="h-28 w-28 rounded-md object-cover"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              );
            })}
          </ol>
        )}
      </section>

      {recipe.notes !== null && recipe.notes.trim().length > 0 && (
        <section data-testid="recipe-notes" className="whitespace-pre-wrap text-sm text-zinc-300 border-t border-zinc-800 pt-4">
          <h2 className="text-base font-medium text-zinc-100 mb-2">Notes</h2>
          {recipe.notes}
        </section>
      )}

      {/* Sticky bottom action bar (Plan 13 Task 11). Last in flow so its
          `sticky bottom-0` naturally pins to the bottom of the scrollable
          content area — above the mobile tab bar (AppShell's `mb-tabbar`
          margin already reserves that space) and padded for the iOS/Android
          gesture bar via `pb-safe`. Tasks 12/13/14 extend this same bar —
          keep new actions inside this flex row. */}
      <div className="sticky bottom-0 z-30 -mx-4 md:-mx-8 mt-4 flex items-center gap-2 border-t border-zinc-800 bg-zinc-950/95 px-4 py-3 pb-safe backdrop-blur md:px-8">
        <Button
          size="cook"
          className="flex-1"
          onClick={() => { void startCooking(); }}
          disabled={starting}
        >
          {starting ? "Starting…" : "Start Cooking"}
        </Button>

        <Button
          variant="outline"
          size="icon-xl"
          aria-label="Send to shopping list"
          onClick={() => { setShopOpen(true); }}
        >
          <ShoppingCart aria-hidden />
        </Button>

        <Button
          variant={recipe.wantToTry ? "default" : "outline"}
          size="icon-xl"
          aria-pressed={!!recipe.wantToTry}
          aria-label={recipe.wantToTry ? "Remove from Want to Try" : "Add to Want to Try"}
          onClick={() => { void toggleWantToTry(); }}
        >
          <Bookmark aria-hidden className={recipe.wantToTry ? "fill-current" : ""} />
        </Button>

        <Button asChild variant="outline" size="icon-xl" aria-label="Edit recipe">
          <Link to={`/recipes/${recipe.id}/edit`}>
            <Pencil aria-hidden />
          </Link>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon-xl" aria-label="More actions">
              <MoreVertical aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem asChild>
              <Link to={`/recipes/${recipe.id}/cook-log`}>
                Cook log ({recipe.stats?.cookCount ?? 0})
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={() => { setConfirmOpen(true); }}
            >
              <Trash2 aria-hidden />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <SendToShoppingSheet recipe={recipe} factor={factor} open={shopOpen} onOpenChange={setShopOpen} />
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this recipe?</DialogTitle>
            <DialogDescription>
              &lsquo;{recipe.title}&rsquo; will be removed from your library. You can recover it within 30 days
              via the runbook (administrative).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); }} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={() => { void confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
